import { useEffect, useMemo, useState } from 'react';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import type { PcaReportFilters } from 'c-components/HiddenReport/reportFilters';
import { esFieldFilters, pcaReportFieldFilters } from 'c-components/HiddenReport/reportFilters';
import { ElasticSearchHistogramInterval, EsPCAReportField } from 'c-sdk';
import { useDispatch } from 'react-redux';
import { callApi } from 'c-data-layer';

import type { CustomReportSlideConfig } from './customReportTypes';
import {
    buildDayRows,
    buildEnvFormatRows,
    buildHourRows,
    buildMetricCard,
    buildMultiGroupRows,
    buildSummaryRows,
    collectRequestedGroupByCombos,
    cumulative,
    fmtInt,
    formatDate,
    formatTime,
    getBucket,
    getGroupByFieldsKey,
    GROUP_BY_FIELD_TO_ES_FIELD,
    normalizeMultiplierRows,
    reformatDateForFilteredDates,
    sortByMagnitude,
} from './customReportDataLib';

export type CustomReportPayload = {
    campaign_id: number;
    jwt: string;
    powerpoint_template: 'Linear_report' | 'DSP_report' | 'custom';
    skin_id: number;
    environment: 'local' | 'production' | 'staging';
    email: string;
    language: 'GB' | 'US';
    start_date?: string;
    end_date?: string;
    third_color?: string;
    filters?: PcaReportFilters;
};

export type RunningTotalsDatum = { label: string; Total: number; Expected: number };

export type SummaryBarRow = {
    name: string;
    impacts_total: number;
    impacts_expected: number;
    plays_total: number;
    plays_expected: number;
};

export type SummaryMetricCard = {
    title: string;
    performance: number | null;
    totalValue: number;
    expectedValue: number;
    total: string;
    expected: string;
    variance: string;
    varianceValue: number;
};

export type MultiplierRow = {
    display_owner: string;
    display_format: string;
    sum_of_play: number;
    dsp_impressions: number;
    geopath_impressions?: number;
    variance: number;
    dsp_avg_impressions?: number;
    geopath_avg_impressions?: number;
    average_im?: number;
    im_variance?: number;
    variance_percent: number;
};

export type CustomReportResolvedData = {
    brand: string;
    accentBrand: string;
    logoUrl: string;
    title: string;
    referenceText: string;
    liveDates: string;
    reportDates: string;
    /** UL-8220: what the report is narrowed by, for the cover. */
    filters: PcaReportFilters;
    linkHref: string;
    language: 'GB' | 'US';
    routeTranslation: boolean;
    summaryCards: SummaryMetricCard[];
    runningTotals: {
        impacts: RunningTotalsDatum[];
        plays: RunningTotalsDatum[];
    };
    datasets: Record<string, SummaryBarRow[]>;
    mapData: Record<string, number[]>;
    multipliers: {
        geoRows: MultiplierRow[];
        nonGeoRows: MultiplierRow[];
    };
};

export const useCustomReportData = (
    payload: CustomReportPayload,
    slides: CustomReportSlideConfig[],
) => {
    const dispatch = useDispatch();
    const { start, data: campaign } = useAPIClientRequest(ApiClient.Entities.Campaign.get);
    const { start: skinStart, data: skin } = useAPIClientRequest(ApiClient.Entities.Skin.get);
    const { start: pcaSummaryStart, data: pcaSummary } = useAPIClientRequest(
        ApiClient.Reports.pcaReport,
    );
    const { start: histogramStart, data: histogram } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.histogram,
    );
    const { start: envStart, data: env } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.pcaGroupByField,
    );
    const { start: regionStart, data: region } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.pcaGroupByField,
    );
    const { start: conurbationStart, data: conurbation } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.pcaGroupByField,
    );
    const { start: formatStart, data: formatBreakdown } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.pcaGroupByField,
    );
    const { start: envFormatStart, data: envFormatBreakdown } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.pcaGroupByField,
    );
    const { start: dowStart, data: dow } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.pcaGroupByField,
    );
    const { start: hourStart, data: hour } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.pcaGroupByField,
    );
    const { start: geoStart, data: geo } = useAPIClientRequest(
        ApiClient.Reports.geopathImpressionMultipliers,
    );
    const { start: impStart, data: imp } = useAPIClientRequest(
        ApiClient.Reports.impressionMultipliers,
    );
    const { start: fetchMapData, data: mapResp } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.frameIdMapData,
    );
    const requestedGroupByCombos = useMemo(() => collectRequestedGroupByCombos(slides), [slides]);
    const [dynamicDatasets, setDynamicDatasets] = useState<Record<string, SummaryBarRow[]>>({});
    const [dynamicDatasetsReady, setDynamicDatasetsReady] = useState(true);

    useEffect(() => {
        if (!payload?.campaign_id || !payload?.skin_id) return;

        start(payload.campaign_id, [
            'agency',
            'market',
            'market.country',
            'creatives',
            'client',
            'creatives.resolution',
            'creatives.file',
            'date',
            'budgetSummary',
            'budgetSummary.lineItems',
            'budgetSummaries',
            'budgetSummaries.lineItems',
            'notices',
            'errors',
            'creativeThumbnails',
            'bookingSummaries',
            'popShotsCount',
            'missingDisplaysCount',
            'additionalContacts',
        ]);

        skinStart(payload.skin_id);
        histogramStart({
            campaignId: payload.campaign_id,
            field: EsPCAReportField.StartDate,
            interval: ElasticSearchHistogramInterval.Day,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
        });
        fetchMapData({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
        });
        pcaSummaryStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            timeFrame: 'summary' as any,
            fieldFilters: pcaReportFieldFilters(payload.filters),
        });
        envStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.DisplayEnv,
        });
        regionStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.DisplayTvRegion,
        });
        conurbationStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.DisplayConurbation,
        });
        formatStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.DisplayFormat,
        });
        envFormatStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            fields: [EsPCAReportField.DisplayEnv, EsPCAReportField.DisplayFormat],
        });
        dowStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.Day,
        });
        hourStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.Hour,
        });
        geoStart({
            id: payload.campaign_id,
        });
        impStart({
            id: payload.campaign_id,
        });
    }, [
        payload,
        start,
        skinStart,
        histogramStart,
        fetchMapData,
        pcaSummaryStart,
        envStart,
        regionStart,
        formatStart,
        envFormatStart,
        dowStart,
        hourStart,
        geoStart,
        impStart,
        conurbationStart,
    ]);

    useEffect(() => {
        let isCurrent = true;

        if (!payload?.campaign_id) {
            setDynamicDatasets({});
            setDynamicDatasetsReady(true);
        } else if (!requestedGroupByCombos.length) {
            setDynamicDatasets({});
            setDynamicDatasetsReady(true);
        } else {
            setDynamicDatasetsReady(false);

            Promise.all(
                requestedGroupByCombos.map(async fields => {
                    const requestData = ApiClient.ReportsElasticSearch.pcaGroupByField({
                        campaignId: payload.campaign_id,
                        startDate: payload?.start_date ?? null,
                        endDate: payload?.end_date ?? null,
                        fieldFilters: esFieldFilters(payload.filters),
                        fields: fields.map(field => GROUP_BY_FIELD_TO_ES_FIELD[field]),
                    });

                    const [err, , success]: [any, unknown, any] = await dispatch(
                        callApi(requestData.request),
                    );

                    if (err) {
                        throw err;
                    }

                    const raw = success?.data?.data ?? success?.data ?? {};

                    return [
                        getGroupByFieldsKey(fields),
                        sortByMagnitude(buildMultiGroupRows(raw)),
                    ] as const;
                }),
            )
                .then(entries => {
                    if (!isCurrent) return undefined;
                    setDynamicDatasets(Object.fromEntries(entries));
                    setDynamicDatasetsReady(true);
                    return undefined;
                })
                .catch(() => {
                    if (!isCurrent) return undefined;
                    setDynamicDatasets({});
                    setDynamicDatasetsReady(true);
                    return undefined;
                });
        }

        return () => {
            isCurrent = false;
        };
    }, [
        dispatch,
        payload?.campaign_id,
        payload?.start_date,
        payload?.end_date,
        payload?.filters,
        requestedGroupByCombos,
    ]);

    const isReady = useMemo(
        () =>
            !!campaign &&
            !!skin &&
            !!pcaSummary &&
            !!histogram &&
            !!env &&
            !!region &&
            !!formatBreakdown &&
            !!envFormatBreakdown &&
            !!dow &&
            !!hour &&
            !!geo &&
            !!imp &&
            !!mapResp &&
            dynamicDatasetsReady,
        [
            campaign,
            skin,
            pcaSummary,
            histogram,
            env,
            region,
            formatBreakdown,
            envFormatBreakdown,
            dow,
            hour,
            geo,
            imp,
            mapResp,
            dynamicDatasetsReady,
        ],
    );

    useEffect(() => {
        if (isReady) {
            // eslint-disable-next-line no-underscore-dangle
            window.__REACT_READY__ = true;
        }
    }, [isReady]);

    const resolvedData = useMemo<CustomReportResolvedData | null>(() => {
        if (!isReady) return null;

        const brand =
            (skin as any)?.data?.data?.primary_color ||
            (skin as any)?.data?.primary_color ||
            'rgba(31,41,55,1)';
        const accentBrand = payload?.third_color
            ? (skin as any)?.data?.data?.third_color || brand
            : brand;
        const logoUrl = (skin as any)?.data?.data?.logo_path ?? '';
        const campaignData = (campaign as any)?.data?.data;
        const routeTranslation =
            campaignData?.type === 'vistar_uk' || campaignData?.type === 'hivestack_uk';
        const title = campaignData?.name || 'Campaign Report';
        const reference = campaignData?.reference || '';
        const referenceText = reference ? ` (${reference})` : '';
        // DSP campaigns often have no `date` relation; fall back to the campaign's own dates.
        const liveStart = campaignData?.date?.date_start ?? campaignData?.date_start;
        const liveEnd = campaignData?.date?.date_end ?? campaignData?.date_end;
        const liveDates =
            liveStart && liveEnd
                ? `${formatDate(liveStart, payload.language)} – ${formatDate(
                      liveEnd,
                      payload.language,
                  )}`
                : '';
        const reportDates =
            payload.start_date && payload.end_date
                ? `${reformatDateForFilteredDates(
                      payload.start_date,
                      payload.language,
                  )} – ${reformatDateForFilteredDates(payload.end_date, payload.language)}`
                : (() => {
                      const dailyStats = (pcaSummary as any)?.data?.data?.daily_stats;
                      if (dailyStats && typeof dailyStats === 'object') {
                          const keys = Object.keys(dailyStats).sort();
                          if (keys.length) {
                              return `${reformatDateForFilteredDates(
                                  keys[0],
                                  payload.language,
                              )} – ${reformatDateForFilteredDates(
                                  keys[keys.length - 1],
                                  payload.language,
                              )}`;
                          }
                      }
                      return '';
                  })();

        const campaignStats = (pcaSummary as any)?.data?.data?.campaign_stats ?? {};
        const impactsActual = campaignStats?.impacts;
        const impactsExpected = campaignStats?.expected_impacts_in_schedule;
        const playsActual = campaignStats?.sum_of_plays;
        const playsExpected = campaignStats?.expected_plays_in_schedule;
        const timeActual = campaignStats?.sum_of_time;
        const timeExpected = campaignStats?.expected_time_in_schedule;

        const rawHist = (histogram as any)?.data?.data ?? (histogram as any)?.data ?? {};
        const dates = Object.keys(rawHist).sort();
        const daily = dates.map(iso => {
            const bucket = getBucket(rawHist, iso);
            return {
                iso,
                label: formatDate(iso, payload.language),
                impacts_actual: bucket?.impacts ?? 0,
                impacts_expected: bucket?.expected_impacts_in_schedule ?? 0,
                plays_actual: bucket?.sum_of_plays ?? 0,
                plays_expected: bucket?.expected_plays_in_schedule ?? 0,
            };
        });

        const impactsActualSeries = cumulative(daily.map(d => d.impacts_actual));
        const impactsExpectedSeries = cumulative(daily.map(d => d.impacts_expected));
        const playsActualSeries = cumulative(daily.map(d => d.plays_actual));
        const playsExpectedSeries = cumulative(daily.map(d => d.plays_expected));

        const runningTotals = {
            impacts: daily.map((d, i) => ({
                label: d.label,
                Total: impactsActualSeries[i],
                Expected: impactsExpectedSeries[i],
            })),
            plays: daily.map((d, i) => ({
                label: d.label,
                Total: playsActualSeries[i],
                Expected: playsExpectedSeries[i],
            })),
        };

        const datasets = {
            display_owner: sortByMagnitude(
                buildSummaryRows(
                    (pcaSummary as any)?.data?.data?.display_owner ??
                        (pcaSummary as any)?.data?.display_owner ??
                        {},
                ),
            ),
            display_env: sortByMagnitude(
                buildSummaryRows((env as any)?.data?.data ?? (env as any)?.data ?? {}),
            ),
            display_format: sortByMagnitude(
                buildSummaryRows(
                    (formatBreakdown as any)?.data?.data ?? (formatBreakdown as any)?.data ?? {},
                ),
            ),
            display_env_format: buildEnvFormatRows(
                (envFormatBreakdown as any)?.data?.data ?? (envFormatBreakdown as any)?.data ?? {},
            ),
            display_tv_region: sortByMagnitude(
                buildSummaryRows((region as any)?.data?.data ?? (region as any)?.data ?? {}),
            ),
            display_conurbation: sortByMagnitude(
                buildSummaryRows(
                    (conurbation as any)?.data?.data ?? (conurbation as any)?.data ?? {},
                ),
            ),
            day_of_week: buildDayRows(
                (dow as any)?.data?.data ?? (dow as any)?.data ?? {},
                payload.language,
            ),
            hour_of_day: buildHourRows((hour as any)?.data?.data ?? (hour as any)?.data ?? {}),
            ...dynamicDatasets,
        } satisfies Record<string, SummaryBarRow[]>;

        const rawMap = mapResp as any;
        const candidate =
            (rawMap?.data?.data &&
            typeof rawMap.data.data === 'object' &&
            !Array.isArray(rawMap.data.data)
                ? rawMap.data.data
                : rawMap?.data?.reportData ?? rawMap?.reportData ?? {}) || {};

        const keys = Object.keys(candidate);
        let mapData = candidate;
        if (keys.length) {
            const first = candidate[keys[0]];
            const hasLatLngAt12 =
                Array.isArray(first) &&
                typeof first[1] === 'number' &&
                first[1] >= -90 &&
                first[1] <= 90 &&
                typeof first[2] === 'number' &&
                first[2] >= -180 &&
                first[2] <= 180;

            if (hasLatLngAt12) {
                mapData = keys.reduce<Record<string, number[]>>((acc, key) => {
                    const row = candidate[key];
                    if (Array.isArray(row)) {
                        const copy = row.slice();
                        copy[6] = row[1];
                        copy[7] = row[2];
                        acc[key] = copy;
                    }
                    return acc;
                }, {});
            }
        }

        const geoRows = normalizeMultiplierRows(geo);
        const nonGeoRows = normalizeMultiplierRows(imp);

        return {
            brand,
            accentBrand,
            logoUrl,
            title,
            referenceText,
            liveDates,
            reportDates,
            filters: payload.filters ?? {},
            linkHref: `https://app.uniledsoftware.com/campaigns/${payload.campaign_id}`,
            language: payload.language,
            routeTranslation,
            summaryCards: [
                buildMetricCard({
                    title: payload.language === 'GB' ? 'Impacts' : 'Impressions',
                    total: impactsActual,
                    expected: impactsExpected,
                    formatter: fmtInt,
                }),
                buildMetricCard({
                    title: 'Plays',
                    total: playsActual,
                    expected: playsExpected,
                    formatter: fmtInt,
                }),
                buildMetricCard({
                    title: 'Time Played',
                    total: timeActual,
                    expected: timeExpected,
                    formatter: formatTime,
                }),
            ],
            runningTotals,
            datasets,
            mapData,
            multipliers: {
                geoRows,
                nonGeoRows,
            },
        };
    }, [
        campaign,
        formatBreakdown,
        dow,
        env,
        envFormatBreakdown,
        histogram,
        hour,
        geo,
        imp,
        isReady,
        mapResp,
        payload.language,
        payload.start_date,
        payload.end_date,
        payload.campaign_id,
        payload.third_color,
        payload.filters,
        pcaSummary,
        region,
        conurbation,
        skin,
        dynamicDatasets,
    ]);

    return { isReady, data: resolvedData };
};
